import { useCreateFileBo, useDeleteFileByIdBo } from "@/api/file.api";
import { FileTypeEnum } from "@/constants/enums/fileType.enum";
import { toExcelFileType } from "@/hooks/useDocumentFormats";
import { GetOneExpeditionBoResponseDto } from "@/types/api/api-types";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

type ExpeditionDocuments = GetOneExpeditionBoResponseDto["expeditionDocuments"];

const PRIMARY_FILE_TYPE = FileTypeEnum.EXPEDITION_CUSTOM_INVOICE;

/**
 * Replaces the custom invoice (PDF and/or Excel) on an expedition by deleting
 * existing files and uploading a new PDF.
 */
export const useReplaceCustomInvoice = (
  expeditionDocuments: ExpeditionDocuments,
  expeditionId: string,
) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation("boClients");
  const { mutateAsync: createFile, isPending: isReplacing } = useCreateFileBo();
  const { mutateAsync: deleteFile } = useDeleteFileByIdBo();

  const expeditionDocumentsId = expeditionDocuments?.id;
  const documents = expeditionDocuments?.documents ?? [];
  const excelFileType = toExcelFileType(PRIMARY_FILE_TYPE);
  const primaryFile = documents.find((f) => f.type === PRIMARY_FILE_TYPE);
  const excelFile = documents.find((f) => f.type === excelFileType);

  const replaceCustomInvoice = async (selectedFile: File) => {
    if (!expeditionDocumentsId) return;

    try {
      if (primaryFile) await deleteFile(primaryFile.id);
      if (excelFile) await deleteFile(excelFile.id);

      const { uploadUrl } = await createFile({
        fileName: selectedFile.name,
        type: PRIMARY_FILE_TYPE,
        contentType: selectedFile.type,
        expeditionDocumentsId,
        isPrivate: true,
        sendViaApi: true,
      });

      await fetch(uploadUrl, {
        method: "PUT",
        body: selectedFile,
        headers: { "Content-Type": selectedFile.type },
      });

      queryClient.invalidateQueries({ queryKey: ["expedition", "bo", expeditionId] });
      toast.success(t("replaceCustomInvoice.success"));
    } catch {
      toast.error(t("replaceCustomInvoice.error"));
    }
  };

  return { replaceCustomInvoice, isReplacing };
};
